import type { Context } from '@montugroup/data-collection';
import { emitUIEvent, productProductEntitySchemaV1 } from '@montugroup/data-collection';
import { extractShopifyId } from './extractShopifyId';

/** ui_event fields are capped at 500 characters by the schema */
const MAX_FIELD_LENGTH = 500;

/** Search queries are free text, so they are cut shorter than other fields */
export const MAX_SEARCH_QUERY_LENGTH = 100;

/** Removes the query string and hash, so no user input in a URL reaches Snowplow */
export const stripQueryString = (url: string) => url.split(/[?#]/)[0];

export type AddToCartBlockReason =
  | 'sold_out'
  | 'qty_empty'
  | 'moq_min'
  | 'moq_max'
  | 'tga_missing'
  | 'concession_unchecked';

export type LeafioEventProduct = {
  /** Shopify product GID or numeric ID */
  id: string;
  title: string;
};

type UIEventFields = {
  id: string;
  value?: string;
  context_id?: string;
  /** Sent as the com.montu.product/product context, as add_to_cart does */
  product?: LeafioEventProduct;
};

/**
 * Each Leafio event and how its fields map onto the ui_event id, value and context_id columns,
 * which the data team reads per event_name.
 */
const LEAFIO_EVENTS = {
  nav_click: ({ target }: { target: string }) => ({ id: 'header_nav', value: stripQueryString(target) }),
  nav_search_used: () => ({ id: 'header_search' }),
  account_menu_click: ({ target }: { target: string }) => ({ id: 'account_menu', value: stripQueryString(target) }),
  cart_open: () => ({ id: 'cart_drawer' }),
  loyalty_section_click: ({ target }: { target: string }) => ({ id: 'loyalty_tabs', value: target }),
  form_validation_error: ({ form, field, errorType }: { form: string; field: string; errorType: string }) => ({
    id: form,
    value: errorType,
    context_id: field,
  }),
  tab_selected: ({ tab }: { tab: string }) => ({ id: 'collection_tabs', value: tab }),
  top_picks_viewed: ({ itemIds }: { itemIds: string[] }) => ({ id: 'top_picks', value: itemIds.join(',') }),
  pagination_load: ({ collectionHandle, pageNumber }: { collectionHandle: string; pageNumber: number }) => ({
    id: collectionHandle,
    value: String(pageNumber),
  }),
  pagination_load_time: ({ collectionHandle, durationMs }: { collectionHandle: string; durationMs: number }) => ({
    id: collectionHandle,
    value: String(Math.round(durationMs)),
  }),
  search_load_time: ({ durationMs }: { durationMs: number }) => ({
    id: 'plp_search',
    value: String(Math.round(durationMs)),
  }),
  search_error: ({ query, errorType }: { query: string; errorType: string }) => ({
    id: 'plp_search',
    value: query.slice(0, MAX_SEARCH_QUERY_LENGTH),
    context_id: errorType,
  }),
  promo_badge_viewed: ({ promoId, product }: { promoId: string; product: LeafioEventProduct }) => ({
    id: 'promo_badge',
    value: promoId,
    product,
  }),
  promo_badge_click: ({ promoId, product }: { promoId: string; product: LeafioEventProduct }) => ({
    id: 'promo_badge',
    value: promoId,
    product,
  }),
  // The product ID is also sent as the value, so clicks can be queried without joining the product context.
  // Position starts at 0: the row in the product table, or the card in a carousel
  product_click: ({
    containerId,
    position,
    product,
  }: {
    containerId: string;
    position: number;
    product: LeafioEventProduct;
  }) => ({
    id: containerId,
    value: String(extractShopifyId(product.id)),
    context_id: String(position),
    product,
  }),
  tga_doc_dropdown_open: ({ product }: { product: LeafioEventProduct }) => ({
    id: 'product_approval_document',
    product,
  }),
  tga_doc_add_new: ({ product }: { product: LeafioEventProduct }) => ({ id: 'product_approval_document', product }),
  tga_doc_upload: ({ state, product }: { state: 'started' | 'completed' | 'failed'; product: LeafioEventProduct }) => ({
    id: 'product_approval_upload',
    value: state,
    product,
  }),
  // Only the approval ID is sent: the option label holds prescriber and patient details
  tga_doc_selected: ({
    source,
    approvalId,
    product,
  }: {
    source: 'existing' | 'new';
    approvalId: string;
    product: LeafioEventProduct;
  }) => ({ id: 'product_approval_document', value: source, context_id: approvalId, product }),
  add_to_cart_blocked: ({ reason, product }: { reason: AddToCartBlockReason; product: LeafioEventProduct }) => ({
    id: 'add_to_cart_button',
    value: reason,
    product,
  }),
  remove_from_cart: ({ product }: { product: LeafioEventProduct }) => ({ id: 'cart_remove', product }),
  // Shopify cart IDs end in a ?key= secret, which is dropped; the token before it joins to the order
  checkout_start: ({ cartId, itemCount }: { cartId: string; itemCount: number }) => ({
    id: 'cart_checkout_button',
    value: stripQueryString(cartId),
    context_id: String(itemCount),
  }),
} satisfies Record<string, (fields: never) => UIEventFields>;

type LeafioEvents = typeof LEAFIO_EVENTS;

const truncate = (value?: string) => value?.slice(0, MAX_FIELD_LENGTH);

const toProductContexts = (product?: LeafioEventProduct): Context[] =>
  product
    ? [
        {
          schema: productProductEntitySchemaV1,
          data: { id: String(extractShopifyId(product.id)), title: truncate(product.title) ?? '' },
        },
      ]
    : [];

/**
 * Sends a Leafio event to Snowplow as a ui_event. Every Leafio dashboard event goes through here.
 * @param eventName - The event, one of LEAFIO_EVENTS
 * @param componentName - The component the event is fired from
 * @param fields - The event's fields, for events that have any
 */
export const trackLeafioEvent = <E extends keyof LeafioEvents>(
  eventName: E,
  componentName: string,
  ...fields: Parameters<LeafioEvents[E]>
) => {
  const mapFields = LEAFIO_EVENTS[eventName] as (...args: unknown[]) => UIEventFields;
  const { id, value, context_id, product } = mapFields(...fields);

  emitUIEvent(
    {
      component_name: componentName,
      event_name: eventName,
      id: truncate(id) ?? '',
      value: truncate(value),
      context_id: truncate(context_id),
    },
    toProductContexts(product),
  );
};
